
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PaginationGlobal from '../Shared/PaginationGlobal';
import CategoryFeatured from './CategoryFeatured';
import { useSecureFetch } from '../../hooks/useSecureFetch';

const SubCategoryProducts = () => {
    const { categorySlug, subCategorySlug } = useParams();
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [sellers, setSellers] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [category, setCategory] = useState({});
    const [subCategory, setSubCategory] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [productsPerPage] = useState(12);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
     const { secureFetch } = useSecureFetch();

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [priceFilter, setPriceFilter] = useState('');

    // Function to shuffle array (Fisher-Yates algorithm)
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // Handle filters
    useEffect(() => {
        let result = [...products];

        // Search filter
        if (searchTerm) {
            result = result.filter(product =>
                product.productName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort by name
        if (sortBy === 'a_to_z') {
            result.sort((a, b) => a.productName.localeCompare(b.productName));
        } else if (sortBy === 'z_to_a') {
            result.sort((a, b) => b.productName.localeCompare(a.productName));
        } else {
            // If no specific sort is selected, randomize the order
            result = shuffleArray(result);
        }

        // Price filter
        if (priceFilter === 'low_to_high') {
            result.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        } else if (priceFilter === 'high_to_low') {
            result.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        }

        setFilteredProducts(result);
        setCurrentPage(1);
    }, [products, searchTerm, sortBy, priceFilter]);

    // Function to get the number of reviews for a specific product
    const getReviewCount = (productId) => {
        return reviews.filter((review) => review.productId === productId).length;
    };

    // Function to calculate average rating for a specific product
    const getAverageRating = (productId) => {
        const productReviews = reviews.filter((review) => review.productId === productId);
        const totalRating = productReviews.reduce((acc, review) => acc + review.rating, 0);
        return productReviews.length > 0 ? (totalRating / productReviews.length).toFixed(1) : 0;
    };

    // Pagination logic
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Get seller info
    const getSeller = (sellerEmail) => {
        return sellers.find(seller => seller.UserEmail === sellerEmail);
    };

    // Reset filters
    const resetFilters = () => {
        setSearchTerm('');
        setSortBy('');
        setPriceFilter('');
    };

    useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(`Fetching data for category: ${categorySlug}, subcategory: ${subCategorySlug}`);

        // 1. Fetch products
        const productsRes = await fetch(`https://server.enjoywiki.com/flip-server/category-sub?subCategorySlug=${subCategorySlug}`);
        if (!productsRes.ok) {
          throw new Error(`Failed to fetch products: ${productsRes.status}`);
        }
        const productsData = await productsRes.json();
        console.log(`Fetched ${productsData.length} products from API`);

        const publishedProducts = productsData.filter(p => p.productStatus === 'Published');
        console.log(`Found ${publishedProducts.length} published products`);

        setProducts(publishedProducts);
        setFilteredProducts(publishedProducts);

        // 2. Fetch subcategory
        try {
          const subCategoryRes = await fetch(`https://server.enjoywiki.com/flip-server/subcategories?slug=${subCategorySlug}`);
          if (subCategoryRes.ok) {
            const subCategoryData = await subCategoryRes.json();
            if (subCategoryData && subCategoryData.length > 0) {
              setSubCategory(subCategoryData[0]);
            }
          }

          // 3. Fetch category
          const categoryRes = await fetch(`https://server.enjoywiki.com/flip-server/category/${categorySlug}`);
          if (categoryRes.ok) {
            const categoryData = await categoryRes.json();
            setCategory(categoryData);
          }

          // 4. Fetch sellers and reviews using secureFetch for both
          const [sellersRes, reviewsRes] = await Promise.all([
            secureFetch('https://server.enjoywiki.com/flip-server/users'),
            secureFetch('https://server.enjoywiki.com/flip-server/product-reviews')
          ]);

          if (Array.isArray(sellersRes)) {
            setSellers(sellersRes);
          }

          if (Array.isArray(reviewsRes)) {
            setReviews(reviewsRes);
          }
        } catch (secondaryError) {
          console.error('Error fetching secondary data:', secondaryError);
          // Skip setting error — we still have products
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
        setError(error.message);
        setProducts([]);
        setFilteredProducts([]);
      } finally {
        setLoading(false);
      }
    };

    if (subCategorySlug) {
      fetchInitialData();
    } else {
      setLoading(false);
      setError('Missing subcategory parameter');
    }
  }, [subCategorySlug, categorySlug, secureFetch]);

    return (
        <>
            <Helmet>
                <title>{`${subCategory.SEOTitle || subCategory.subCategoryName || 'Products'} | FlipBundle.com`}</title>
                <meta name="description" content={subCategory.subCategoryDetails || "Browse our selection of products in this subcategory."} />
            </Helmet>
            <section
                className="w-breadcrumb-area"
                style={{
                    backgroundImage:
                        "url(/assets/img/breadcrumb-bg.svg)",
                }}
            >
                <div className="container">
                    <div className="row">
                        <div className="col-auto">
                            <div
                                className="position-relative z-2 aos-init">
                                <h1 className="section-title-light mb-2">{subCategory.subCategoryName || 'Products'}</h1>
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb w-breadcrumb">
                                        <li className="breadcrumb-item">
                                            <Link to="/">Home</Link>
                                        </li>
                                        {categorySlug && (
                                            <li className="breadcrumb-item">
                                                <Link to={`/category/${categorySlug}`}>{category.categoryName || categorySlug}</Link>
                                            </li>
                                        )}
                                        <li className="breadcrumb-item active" aria-current="page">
                                            {subCategory.subCategoryName || subCategorySlug}
                                        </li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="blog-page-area pd-top-100 pd-bottom-100">
                {subCategory.subCategoryDetails && (
                    <div className='container'>
                        <p className='mt-5 mb-5'>{subCategory.subCategoryDetails}</p>
                    </div>
                )}
                <CategoryFeatured />
                <div className="container">
                    <div className="container-fluid py-4 bg-light rounded shadow-sm mb-4 mt-5">
                        <div className="row justify-content-between align-items-center">
                            <div className="col-12 col-md-auto flex-grow-1 mb-3 mb-md-0">
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control shadow-none border-start-0 ps-0"
                                        placeholder="Search products..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        aria-label="Search products"
                                    />
                                </div>
                            </div>

                            <div className="col-12 col-md-auto">
                                <div className="d-flex gap-3 flex-wrap">
                                    <div className="flex-grow-1">
                                        <select
                                            className="form-control shadow-none bg-white border"
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                            aria-label="Sort by name"
                                        >
                                            <option value="">Sort By Name</option>
                                            <option value="a_to_z">A to Z</option>
                                            <option value="z_to_a">Z to A</option>
                                        </select>
                                    </div>

                                    <div className="flex-grow-1">
                                        <select
                                            className="form-control shadow-none bg-white border"
                                            value={priceFilter}
                                            onChange={(e) => setPriceFilter(e.target.value)}
                                            aria-label="Sort by price"
                                        >
                                            <option value="">Price (Default)</option>
                                            <option value="low_to_high">Low to High</option>
                                            <option value="high_to_low">High to Low</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Active filters display */}
                    {(searchTerm || sortBy || priceFilter) && (
                        <div className="container mb-4">
                            <div className="d-flex align-items-center flex-wrap gap-2">
                                <span className="fw-semibold">Active Filters:</span>
                                
                                {searchTerm && (
                                    <span className="badge bg-primary rounded-pill px-3 py-2 d-flex align-items-center">
                                        Search: {searchTerm}
                                        <button 
                                            className="btn-close btn-close-white ms-2" 
                                            style={{ fontSize: '0.65rem' }} 
                                            onClick={() => setSearchTerm('')}
                                            aria-label="Clear search"
                                        ></button>
                                    </span>
                                )}
                                
                                {sortBy && (
                                    <span className="badge bg-primary rounded-pill px-3 py-2 d-flex align-items-center">
                                        Sort: {sortBy === 'a_to_z' ? 'A to Z' : 'Z to A'}
                                        <button 
                                            className="btn-close btn-close-white ms-2" 
                                            style={{ fontSize: '0.65rem' }} 
                                            onClick={() => setSortBy('')}
                                            aria-label="Clear sort"
                                        ></button>
                                    </span>
                                )}
                                
                                {priceFilter && (
                                    <span className="badge bg-primary rounded-pill px-3 py-2 d-flex align-items-center">
                                        Price: {priceFilter === 'low_to_high' ? 'Low to High' : 'High to Low'}
                                        <button 
                                            className="btn-close btn-close-white ms-2" 
                                            style={{ fontSize: '0.65rem' }} 
                                            onClick={() => setPriceFilter('')}
                                            aria-label="Clear price filter"
                                        ></button>
                                    </span>
                                )}
                                
                                {(searchTerm || sortBy || priceFilter) && (
                                    <button 
                                        className="btn btn-sm btn-outline-secondary ms-2"
                                        onClick={resetFilters}
                                    >
                                        Clear All
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Products Grid */}
                    <div className="row mt-5">
                        <div className="col-lg-12 order-lg-last">
                            <div className="all-item-section all-item-area-2">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading products...</p>
                                    </div>
                                ) : error ? (
                                    <div className="text-center py-5">
                                        <div className="alert alert-danger">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            {error}
                                        </div>
                                        <button 
                                            className="btn btn-primary mt-3"
                                            onClick={() => window.location.reload()}
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                ) : currentProducts.length > 0 ? (
                                    <div className="row">
                                        {currentProducts.map(product => {
                                            const seller = getSeller(product.sellerEmail);
                                            return (
                                                <div className="col-lg-3 col-md-6 mb-4" key={product._id}>
                                                    <div className="service-card bg-white">
                                                        <div className="position-relative">
                                                            <Link to={`/${product.slug}/${product.productId}`}>
                                                                <img
                                                                    src={product.featuredImage || "/assets/img/services/1.png"}
                                                                    className="recently-view-card-img w-100"
                                                                    alt={product.productName}
                                                                    style={{ aspectRatio: '16 / 9', objectFit: 'cover' }}
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = "/assets/img/services/1.png";
                                                                    }}
                                                                />
                                                            </Link>
                                                        </div>
                                                        <div className="service-card-content">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div>
                                                                    <h3 className="service-card-price fw-bold">${product.price}</h3>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <span className="service-card-rating">
                                                                        {getAverageRating(product._id)} ({getReviewCount(product._id)} Reviews)
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <h3 className="service-card-title fw-semibold">
                                                                <Link to={`/${product.slug}/${product.productId}`}>
                                                                    {product.productName}
                                                                </Link>
                                                            </h3>
                                                            <div className="d-flex align-items-center service-card-author">
                                                                <img
                                                                    src={seller?.profileURL || "https://raw.githubusercontent.com/Shah-Limon/em-list/master/images-dash/profile.jpg"}
                                                                    className="service-card-author-img"
                                                                    alt=""
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = "https://raw.githubusercontent.com/Shah-Limon/em-list/master/images-dash/profile.jpg";
                                                                    }}
                                                                />
                                                                <Link to={`/profile-seller/${seller?._id || 'seller'}`} className="service-card-author-name">
                                                                    {seller?.userName || product.sellerEmail.split('@')[0]}
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <svg
                                            viewBox="0 0 240 200"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-40 h-40 mx-auto mb-4"
                                            style={{ maxWidth: '160px' }}
                                        >
                                            <rect x="40" y="30" width="160" height="140" fill="#f3f4f6" rx="8" />
                                            <path d="M60 60 H180" stroke="#d1d5db" strokeWidth="4" strokeLinecap="round" />
                                            <path d="M60 90 H180" stroke="#d1d5db" strokeWidth="4" strokeLinecap="round" />
                                            <path d="M60 120 H140" stroke="#d1d5db" strokeWidth="4" strokeLinecap="round" />
                                            <circle cx="160" cy="140" r="40" fill="none" stroke="#9ca3af" strokeWidth="4" />
                                            <line x1="188" y1="168" x2="200" y2="180" stroke="#9ca3af" strokeWidth="4" strokeLinecap="round" />
                                            <path d="M150 140 H170" stroke="#9ca3af" strokeWidth="4" strokeLinecap="round" />
                                        </svg>
                                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Products Found</h3>
                                        <p className="text-gray-500 mb-4">
                                            We couldn't find any products in this subcategory matching your search criteria.
                                        </p>
                                        {(searchTerm || sortBy || priceFilter) && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={resetFilters}
                                            >
                                                Clear Filters
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Pagination */}
                                {!loading && !error && filteredProducts.length > productsPerPage && (
                                    <div className='text-center mb-5'>
                                        <PaginationGlobal
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={paginate}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default SubCategoryProducts;